function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useMemo, useEffect } from 'react';
import compact from 'lodash/compact';
import { Typography, IconButton, RichTextViewer } from 'seagull';
import { EyeLine } from 'seagull/icons';
import RichTextViewerModal from "../../RichTextViewerModal";
import { useGlobalContext, useIoContext, useStore, useStoreActions } from "../../../contexts";
import { getAgreementData } from "../../ServiceCreation/ServiceCreation.utils";
import StatementOfWork from "./components/StatementOfWork";
import { AgreementViewCard, SpecificTerm } from "./components/CommonComponents";
import Header from "./components/Header";
import styles from "./AgreementView.module.scss";
import { legalTermsModel, relationshipModel } from "../../../models";
import SignedHeader from "./components/SignedHeader";
import ChangesSummary from "./components/ChangesSummary";
import AgreementAmendmentsApprovalMethod from "./components/AgreementAmendmentsApprovalMethod";
import { getAllowManualPaymentsText, getCoverCCFeesChangeBullet, getCoverCCFeesText, getNetTermsChangeBullet } from "../../../business-logic/amendmentsLogic";
import { netTermsToString } from "../../../infra/utils";
import AmendmentChangesInAgreementView from "./components/AmendmentChangesInAgreementView";
import { selectors } from "../../../store/state-selectors";
import AgreementInvoicesApprovalMethod from "./components/AgreementInvoicesApprovalMethod";
const autoprefix = 'agreement-view-';
const pdfViewUrlLoader = 'pdfViewUrl';
export default function AgreementView(props) {
  const {
    relationship,
    isVendor,
    refresh
  } = props;
  const globalContext = useGlobalContext();
  const {
    openNewTab
  } = useIoContext();
  const {
    extraClause
  } = relationship;
  const allowManualPayments = relationshipModel.actualAllowManualPayments(relationship);
  const {
    buttonPrimary
  } = useStore(selectors.user.branding);
  const qboClasses = useStore(state => state.qboClasses.data);
  const {
    api,
    fetchQboClasses
  } = useStoreActions();
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [legalTermsUrl, setLegalTermsUrl] = useState();
  const updatedNetTerms = relationshipModel.updatedNetTerms(relationship);
  const netTermsChangeBullet = updatedNetTerms ? getNetTermsChangeBullet(relationship, updatedNetTerms.value) : null;
  const updatedIsVendorCoveringCCFee = relationshipModel.updatedIsVendorCoveringCCFee(relationship);
  useEffect(() => {
    fetchQboClasses();
  }, []);
  const coverCCFeesChangeBullet = updatedIsVendorCoveringCCFee ? getCoverCCFeesChangeBullet(relationship, updatedIsVendorCoveringCCFee.value) : null;
  const richText = useMemo(() => {
    return legalTermsModel.richTextContent(relationship.legalTerms);
  }, [relationship]);
  const agreementData = useMemo(() => {
    return getAgreementData(relationship);
  }, [relationship]);
  async function onViewLegalTerms() {
    if (legalTermsModel.isPDF(relationship.legalTerms)) {
      const url = await getLegalTermsUrl(relationship.legalTerms);
      openNewTab(url);
    } else {
      setShowViewerModal(true);
    }
  }
  async function getLegalTermsUrl(legalTerms) {
    if (legalTermsUrl) {
      return legalTermsUrl;
    }
    globalContext.startLoader(pdfViewUrlLoader);
    const pdfViewUrl = await api.business.getLegalTermsDownloadURL(legalTerms.pdfUniqueName, relationship.vendor.id);
    if (!pdfViewUrl) {
      throw new Error('legalTermsUrlError');
    }
    setLegalTermsUrl(pdfViewUrl);
    globalContext.stopLoader(pdfViewUrlLoader);
    return pdfViewUrl;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Header, {
    relationship: relationship,
    onUpdate: refresh
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.main,
    "data-testid": "agreement-view"
  }, /*#__PURE__*/React.createElement(ChangesSummary, {
    isVendor: isVendor,
    relationship: relationship,
    testid: autoprefix + 'changes',
    refresh: refresh
  }), /*#__PURE__*/React.createElement(SignedHeader, {
    relationship: relationship,
    brandColor: buttonPrimary,
    testPrefix: autoprefix
  }), /*#__PURE__*/React.createElement(AgreementViewCard, {
    headTitle: "Services Provided"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.services
  }, /*#__PURE__*/React.createElement(StatementOfWork, {
    agreement: relationship,
    services: relationship.services,
    isVendor: isVendor,
    agreementData: agreementData,
    qboClasses: qboClasses
  }))), /*#__PURE__*/React.createElement(AgreementViewCard, {
    headTitle: "Terms and conditions"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.termsAndConditions
  }, /*#__PURE__*/React.createElement(Typography, null, relationship.legalTerms.name), /*#__PURE__*/React.createElement(IconButton, {
    noPadding: true,
    skin: "primary",
    size: "small",
    "data-testid": autoprefix + "btn-view",
    onClick: onViewLegalTerms
  }, /*#__PURE__*/React.createElement(EyeLine, null)))), /*#__PURE__*/React.createElement(AgreementViewCard, {
    headTitle: "Specific terms"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.specificTerms
  }, /*#__PURE__*/React.createElement(SpecificTerm, {
    field: "Payment terms",
    value: netTermsToString(relationshipModel.actualNetTerms(relationship)),
    testid: autoprefix + 'net-terms',
    changes: netTermsChangeBullet && /*#__PURE__*/React.createElement(AmendmentChangesInAgreementView, {
      disableBorderRadius: true,
      items: [netTermsChangeBullet],
      autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
      testid: autoprefix + 'net-terms-changes'
    })
  }), compact([relationship.effectiveDate && {
    field: 'Effective date of agreement',
    value: relationshipModel.formatEffectiveDate(relationship),
    testid: autoprefix + 'effective-date'
  }, allowManualPayments && {
    field: 'Manual approval of each payment',
    value: getAllowManualPaymentsText(allowManualPayments),
    testid: autoprefix + 'manual-payments'
  }]).map(data => /*#__PURE__*/React.createElement(SpecificTerm, _extends({
    key: data.field
  }, data))), /*#__PURE__*/React.createElement(SpecificTerm, {
    testid: autoprefix + 'cc-fees',
    field: "Credit card fees",
    value: getCoverCCFeesText(relationshipModel.actualIsVendorCoveringCCFee(relationship), relationshipModel.vendorName(relationship), relationshipModel.customerName(relationship)),
    changes: coverCCFeesChangeBullet && /*#__PURE__*/React.createElement(AmendmentChangesInAgreementView, {
      disableBorderRadius: true,
      items: [coverCCFeesChangeBullet],
      autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
      testid: autoprefix + 'cc-fees-changes'
    })
  }), /*#__PURE__*/React.createElement(AgreementAmendmentsApprovalMethod, {
    relationship: relationship,
    testid: autoprefix
  }), /*#__PURE__*/React.createElement(AgreementInvoicesApprovalMethod, {
    relationship: relationship,
    testid: autoprefix
  }), extraClause && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    weight: "bold"
  }, "Notes"), /*#__PURE__*/React.createElement(RichTextViewer, {
    "data-testid": 'extra-clause-rich-text-viewer',
    value: extraClause,
    skin: "secondary"
  })), showViewerModal && /*#__PURE__*/React.createElement(RichTextViewerModal, {
    show: true,
    title: relationship.legalTerms.name,
    text: richText,
    onCancel: () => setShowViewerModal(false)
  })))));
}